import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import { format } from 'date-fns';
import { Notification, PaginatedResponse } from '../../models';
import { API_URL } from '../../services/api';
import NotificationDetails from './NotificationDetails'; // This will be created next

const NotificationsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = async (pageNum = page, size = pageSize, search = searchTerm) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_URL || API_URL}/connect/notifications/?page=${pageNum + 1}&page_size=${size}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data: PaginatedResponse<Notification> = await res.json();
      setNotifications(data.results);
      setTotalCount(data.count);
    } catch (e) {
      setNotifications([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchNotifications(page, pageSize, searchTerm);
    // eslint-disable-next-line
  }, [page, pageSize, searchTerm]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleViewDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const getStatusChip = (status: string) => {
    let color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning';
    let icon = null;
    
    switch (status) {
      case 'scheduled':
        color = 'secondary';
        icon = <HourglassEmptyIcon fontSize="small" />;
        break;
      case 'sending':
        color = 'warning';
        icon = <SendIcon fontSize="small" />;
        break;
      case 'sent':
        color = 'info';
        icon = <SendIcon fontSize="small" />;
        break;
      case 'delivered':
        color = 'primary';
        icon = <DoneIcon fontSize="small" />;
        break;
      case 'read':
        color = 'success';
        icon = <DoneIcon fontSize="small" />;
        break;
      case 'failed':
        color = 'error';
        icon = <ErrorIcon fontSize="small" />;
        break;
      case 'canceled':
        color = 'default';
        break;
      default:
        color = 'default';
    }

    return (
      <Chip
        icon={icon as React.ReactElement}
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={color}
        size="small"
      />
    );
  };

  const columns: GridColDef[] = [
    { 
      field: 'title', 
      headerName: 'Title', 
      flex: 1,
      minWidth: 180 
    },
    { 
      field: 'template_name', 
      headerName: 'Template', 
      width: 200
    },
    {
      field: 'channel_name',
      headerName: 'Channel',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<Notification, any>) => 
        getStatusChip(params.value),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (value) => {
        return value ? format(new Date(value), 'MMM d, yyyy HH:mm') : '-';
      },
    },
    {
      field: 'sent_at',
      headerName: 'Sent',
      width: 170,
      valueFormatter: (value) => {
        return value ? format(new Date(value), 'MMM d, yyyy HH:mm') : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 100,
      renderCell: (params: GridRenderCellParams<Notification, any>) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton onClick={() => handleViewDetails(params.row)} size="small">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification History
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        View history of all sent notifications
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search notifications by title, content, template, channel or status"
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      <Paper elevation={2} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={notifications}
          columns={columns}
          pagination
          pageSizeOptions={[25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading}
          rowCount={totalCount}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'created_at', sort: 'desc' }],
            },
          }}
        />
      </Paper>

      {selectedNotification && (
        <NotificationDetails
          open={detailsOpen}
          notification={selectedNotification}
          onClose={handleCloseDetails}
        />
      )}
    </Box>
  );
};

export default NotificationsList;
